import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'

import { queryKeys } from './queryKeys'

export type InsertCategory = Database['public']['Tables']['category']['Insert']
export type Category_ = Database['public']['Tables']['category']['Row']
export type InsertCategoryProduct =
  Database['public']['Tables']['category_product']['Insert']
export type CategoryProduct_ =
  Database['public']['Tables']['category_product']['Row']

export const useCategoryApi = ({
  category_id,
  level,
}: {
  category_id?: number | null
  level?: number
}) => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const { data: categories } = useQuery({
    queryKey: [queryKeys.categories, category_id],
    queryFn: async () => {
      let query = supabase
        .from('category')
        .select('id, title')
        .order('title', { ascending: true })

      // Level 1: "Alla kategorier"
      if (level === 1) {
        query = query.is('parent_category_id', null)
      }

      // Level 2: "Outdoor & Livsstil"
      else if (level === 2) {
        query = query.eq('parent_category_id', category_id!)
      }

      // Level 3: "Hem & Boende"
      else if (level === 3) {
        query = query.eq('parent_category_id', category_id!)
      }

      // Level 4: "Köksaccessoarer"
      else if (level === 4) {
        query = query.eq('id', category_id!)
      }

      return await query
    },
  })

  const { data: allCategories } = useQuery({
    queryKey: [queryKeys.categories, 'all'],
    queryFn: async () => {
      let query = supabase
        .from('category')
        .select(`*`)
        .order('id', { ascending: true })

      return await query
    },
  })

  // Get products in a category (for admin use)
  const getCategoryProducts = (categoryId: number | null) => {
    return useQuery({
      queryKey: [queryKeys.categories, 'products', categoryId],
      queryFn: async () => {
        if (!categoryId) return { productIds: [], productCount: 0 }

        // Get product IDs from category_product table
        const { data: categoryProducts, error } = await supabase
          .from('category_product')
          .select('product_id')
          .eq('category_id', categoryId)

        if (error) throw error

        const productIds = (categoryProducts || [])
          .map((cp) => cp.product_id)
          .filter((id): id is number => id !== null)

        // Get product details
        const { data: products, error: productsError } = await supabase
          .from('product')
          .select('id, article_number, item_name')
          .in('id', productIds)

        if (productsError) throw productsError

        return {
          productIds,
          products: products || [],
          productCount: productIds.length,
        }
      },
      enabled: !!categoryId,
    })
  }

  const createCategory = useMutation({
    mutationFn: async (insertCategory: InsertCategory) =>
      await supabase
        .from('category')
        .insert(insertCategory)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
    },
  })

  const createCategoryProduct = useMutation({
    mutationFn: async (insert: InsertCategoryProduct) =>
      await supabase
        .from('category_product')
        .insert(insert)
        .select('id')
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
    },
  })

  const deleteCategoryProduct = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('category_product').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
    },
  })

  const updateCategoryProduct = useMutation({
    mutationFn: async (insertCategoryProduct: InsertCategoryProduct) =>
      await supabase
        .from('category_product')
        .update(insertCategoryProduct)
        .eq('id', insertCategoryProduct.id!),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
    },
  })

  const updateCategory = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number
      updates: Database['public']['Tables']['category']['Update']
    }) =>
      await supabase
        .from('category')
        .update(updates)
        .eq('id', id)
        .select()
        .single(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
    },
  })

  const deleteCategory = useMutation({
    mutationFn: async (id: number) =>
      await supabase.from('category').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
    },
  })

  // Bulk move products from one category to another
  const bulkMoveProducts = useMutation({
    mutationFn: async ({
      fromCategoryId,
      toCategoryId,
      productIds,
    }: {
      fromCategoryId: number
      toCategoryId: number
      productIds: number[]
    }) => {
      if (productIds.length === 0) {
        return { updated: 0, duplicatesRemoved: 0 }
      }

      // Get all category_product entries for the products in the source category
      const { data: entriesToMove, error: fetchError } = await supabase
        .from('category_product')
        .select('id, product_id')
        .eq('category_id', fromCategoryId)
        .in('product_id', productIds)

      if (fetchError) throw fetchError

      if (!entriesToMove || entriesToMove.length === 0) {
        return { updated: 0, duplicatesRemoved: 0 }
      }

      // Check for existing relationships with target category
      const productIdsSet = new Set(
        entriesToMove
          .map((e) => e.product_id)
          .filter((id): id is number => id !== null)
      )

      const { data: existingRelations, error: existingError } = await supabase
        .from('category_product')
        .select('product_id')
        .eq('category_id', toCategoryId)
        .in('product_id', Array.from(productIdsSet))

      if (existingError) throw existingError

      const existingProductIds = new Set(
        (existingRelations || [])
          .map((r) => r.product_id)
          .filter((id): id is number => id !== null)
      )

      // Separate entries into: update vs delete (duplicates)
      const toUpdate: number[] = []
      const toDelete: number[] = []

      entriesToMove.forEach((entry) => {
        if (entry.product_id && existingProductIds.has(entry.product_id)) {
          // Product already exists in target category - delete from source
          toDelete.push(entry.id)
        } else if (entry.product_id) {
          // Update to point to target category
          toUpdate.push(entry.id)
        }
      })

      let updated = 0
      if (toUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('category_product')
          .update({ category_id: toCategoryId })
          .in('id', toUpdate)

        if (updateError) throw updateError
        updated = toUpdate.length
      }

      let duplicatesRemoved = 0
      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('category_product')
          .delete()
          .in('id', toDelete)

        if (deleteError) throw deleteError
        duplicatesRemoved = toDelete.length
      }

      return { updated, duplicatesRemoved }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.categories] })
      queryClient.invalidateQueries({ queryKey: [queryKeys.productsClosed] })
    },
  })

  return {
    categories,
    allCategories,
    createCategory,
    createCategoryProduct,
    deleteCategoryProduct,
    updateCategory,
    deleteCategory,
    updateCategoryProduct,
    getCategoryProducts,
    bulkMoveProducts,
  }
}
